import React, { useEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';

export interface SelectorOption {
  title: string;
  value: string;
  active?: boolean;
  isDefaultProject?: boolean; // Add support for default project indicator
}

export interface ProjectSelectorProps {
  placeholder?: string;
  selectedItem: string | null;
  options: Array<SelectorOption>;
  onChange: (id: SelectorOption, isArchive?: boolean) => void;
  addNewItemTitle: string;
  addNewItemHandler?: () => void;
  showArchiveButton?: boolean;
}

export enum ArchiveSelectorOption {
  Archive = 'archive',
}

const ProjectSelector = ({
  placeholder = 'Choose',
  options,
  onChange,
  selectedItem,
  addNewItemTitle,
  addNewItemHandler,
  showArchiveButton,
}: ProjectSelectorProps) => {
  const [selectedValue, setSelectedValue] = useState<SelectorOption>({
    title: placeholder,
    value: '',
  });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedItem && options?.length) {
      const foundItem = options?.find((item) => item.value === selectedItem);
      if (foundItem) {
        setSelectedValue(foundItem);
      }
    }
  }, [selectedItem, options]);

  return (
    <Menu as="div" className="relative font-sofiaSans">
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            className="border-secondaryStoryLightBlueText border-2 border-opacity-20 text-secondaryStoryBlueText font-bold text-lg leading-none py-2 pl-3 pr-2 rounded-lg flex justify-between items-center min-w-[125px] xs:min-w-[175px] max-w-[200px] flex-nowrap whitespace-nowrap text-ellipsis w-full"
          >
            <span className="pt-0.5 flex justify-start flex-nowrap whitespace-nowrap text-ellipsis w-full">
              {selectedValue?.title}
            </span>
            <div>
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </Menu.Button>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-auto md:left-0 right-0 md:right-auto mt-2 w-[250px] bg-white rounded shadow-lg z-20 focus:outline-none font-sofiaSans">
              <div className="flex justify-between items-center px-4 py-2 border-b">
                <span className="text-lg font-medium text-black">{placeholder}</span>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                  onClick={() => buttonRef.current?.click()}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className={'flex flex-col max-h-screen md:max-h-[40vh] overflow-y-auto'}>
                {options &&
                  options.map((option, index) => (
                    <Menu.Item key={index}>
                      {({ active }) => (
                        <div
                          className={`px-4 py-2 border-b cursor-pointer text-black flex justify-between items-center text-lg font-medium hover:bg-gray-100 ${active ? 'bg-gray-100' : ''}`}
                          onClick={() => {
                            setSelectedValue(option);
                            onChange(option);
                            buttonRef.current?.click();
                          }}
                        >
                          <span>{option.title}</span>
                          {option.isDefaultProject && (
                            <div className="flex items-center text-yellow-500" title="Active project">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                {showArchiveButton && (
                  <Menu.Item key={9999}>
                    {({ active }) => (
                      <div
                        className={`px-4 py-2 border-b cursor-pointer text-storyblueSecondLightText flex justify-start text-lg font-medium hover:bg-gray-100 ${active ? 'bg-gray-100' : ''}`}
                        onClick={() => {
                          setSelectedValue({ title: 'Show Archive', value: ArchiveSelectorOption.Archive });
                          onChange({ title: 'Show Archive', value: ArchiveSelectorOption.Archive }, true);
                          buttonRef.current?.click();
                        }}
                      >
                        <span>Show Archive</span>
                      </div>
                    )}
                  </Menu.Item>
                )}
              </div>
              {addNewItemHandler && (
                <div className="px-4 py-4">
                  <button
                    className="w-full flex items-center justify-between text-white py-2 px-6 rounded-full font-semibold text-base bg-storyblueSecondLightText transition"
                    onClick={() => {
                      addNewItemHandler();
                      buttonRef.current?.click();
                    }}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="flex justify-center items-center w-full">{addNewItemTitle}</span>
                  </button>
                </div>
              )}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default ProjectSelector;
